import styled from '@emotion/styled';
import { DateField, FieldError, NumberField, SelectField, SettingsRow, uiTheme as t } from 'Components/ui';
import { describeRule } from 'Utils/courseVigency';
import type { CourseVigencyI, ExpirationRuleI, ExpirationRuleTypeT } from 'types';

interface CourseVigencyEditorProps {
  value: CourseVigencyI;
  onChange: (value: CourseVigencyI) => void;
  errors?: string[];
  disabled?: boolean;
}

interface LabeledFieldProps {
  id: string;
  label: string;
  children: React.ReactNode;
}

const TYPE_OPTIONS = [
  { value: 'none', label: 'Indefinido' },
  { value: 'interval', label: 'Cada cierto tiempo' },
  { value: 'weekday', label: 'Un día de la semana' },
  { value: 'monthday', label: 'Un día del mes' },
  { value: 'range', label: 'Rango de fechas' },
];

const CYCLE_UNIT_OPTIONS = [
  { value: 'day', label: 'días' },
  { value: 'week', label: 'semanas' },
  { value: 'month', label: 'meses' },
  { value: 'year', label: 'años' },
];

const DUE_UNIT_OPTIONS = [
  { value: 'day', label: 'días' },
  { value: 'week', label: 'semanas' },
  { value: 'month', label: 'meses' },
];

const WEEKDAY_OPTIONS = [
  { value: '1', label: 'Lunes' },
  { value: '2', label: 'Martes' },
  { value: '3', label: 'Miércoles' },
  { value: '4', label: 'Jueves' },
  { value: '5', label: 'Viernes' },
  { value: '6', label: 'Sábado' },
  { value: '7', label: 'Domingo' },
];

const Fields = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.75rem',
  '& > *': { flex: '1 1 8rem', minWidth: 0 },
});

const FieldLabel = styled.label({
  display: 'block',
  minWidth: 0,
});

const FieldName = styled.span({
  display: 'block',
  marginBottom: '0.25rem',
  fontFamily: t.font,
  fontSize: '0.75rem',
  color: t.color.muted,
});

const Note = styled.p({
  margin: '0.5rem 0 0',
  fontFamily: t.font,
  fontSize: '0.78125rem',
  lineHeight: 1.45,
  color: t.color.muted,
});

const Summary = styled.p({
  margin: 0,
  fontFamily: t.font,
  fontSize: '0.8125rem',
  color: t.color.text,
});

/**
 * Etiqueta visible sobre un control del editor.
 *
 * El nombre sale de `label/htmlFor` y no de un `aria-label`: así se ve en
 * pantalla —tres campos en fila sin rótulo no dicen cuál es cuál— y el clic
 * enfoca el control, que aquí siempre es `input` o `select`.
 */
const LabeledField = ({ id, label, children }: LabeledFieldProps) => (
  <FieldLabel htmlFor={id}>
    <FieldName>{label}</FieldName>
    {children}
  </FieldLabel>
);

const errorId = (index: number) => `course-vigency-error-${index + 1}`;
const mergeDescribedBy = (...values: Array<string | undefined>) =>
  Array.from(new Set(values.filter(Boolean).flatMap(value => String(value).split(' ')))).join(' ') || undefined;

/**
 * Editor controlado de la vigencia de un curso.
 *
 * Expone el ciclo de repetición y el plazo del curso sin guardar estado
 * propio; toda la autoridad del valor vive en el padre.
 */
const CourseVigencyEditor = ({ value, onChange, errors = [], disabled }: CourseVigencyEditorProps) => {
  const rule = value.expirationRule || ({ type: 'none' } as ExpirationRuleI);
  const allErrorDescribedBy = errors.map((_, index) => errorId(index)).join(' ') || undefined;

  const emit = (next: Partial<CourseVigencyI>) => onChange({ ...value, ...next });
  const emitRule = (next: ExpirationRuleI) => emit({ expirationRule: next });

  /**
   * Cambia el tipo de ciclo conservando lo que aún tenga sentido.
   *
   * La fecha de arranque sirve tanto para `interval` como para `range`; el
   * ancla solo se reutiliza entre reglas basadas en día.
   */
  const changeType = (type: string) => {
    const next: ExpirationRuleI = { type: type as ExpirationRuleTypeT };

    if (type === 'interval') {
      next.interval = rule.interval || 1;
      next.unit = rule.unit || 'month';
      if (rule.start) next.start = rule.start;
    }

    if (type === 'range' && rule.start) {
      next.start = rule.start;
    }

    if ((type === 'weekday' || type === 'monthday') && rule.anchor) {
      next.anchor = rule.anchor;
    }

    emitRule(next);
  };

  const shortMonthWarning = rule.type === 'monthday' && (rule.anchor || 0) >= 29;

  return (
    <>
      <SettingsRow
        label="Ciclo de repetición"
        help="Cada cuánto vuelve a abrirse el curso para el empleado."
        required
        align="start"
      >
        {({ describedById }) => {
          const cycleDescribedBy = mergeDescribedBy(describedById, allErrorDescribedBy);

          return (
            <>
              <Fields>
                <LabeledField id="tipoCiclo" label="Tipo de ciclo">
                  <SelectField
                    id="tipoCiclo"
                    aria-describedby={cycleDescribedBy}
                    value={rule.type}
                    options={TYPE_OPTIONS}
                    disabled={disabled}
                    onChange={changeType}
                  />
                </LabeledField>

                {rule.type === 'interval' && (
                  <>
                    <LabeledField id="cantidadCiclo" label="Cantidad del ciclo">
                      <NumberField
                        id="cantidadCiclo"
                        aria-describedby={cycleDescribedBy}
                        value={rule.interval ?? null}
                        min={1}
                        disabled={disabled}
                        onChange={interval => emitRule({ ...rule, interval: interval ?? undefined })}
                      />
                    </LabeledField>
                    <LabeledField id="unidadCiclo" label="Unidad del ciclo">
                      <SelectField
                        id="unidadCiclo"
                        aria-describedby={cycleDescribedBy}
                        value={rule.unit || ''}
                        options={CYCLE_UNIT_OPTIONS}
                        disabled={disabled}
                        onChange={unit => emitRule({ ...rule, unit: unit as ExpirationRuleI['unit'] })}
                      />
                    </LabeledField>
                    <LabeledField id="inicioCiclo" label="Inicio del primer ciclo">
                      <DateField
                        id="inicioCiclo"
                        aria-describedby={cycleDescribedBy}
                        value={rule.start ?? null}
                        disabled={disabled}
                        onChange={start => emitRule({ ...rule, start: start ?? undefined })}
                      />
                    </LabeledField>
                  </>
                )}

                {rule.type === 'weekday' && (
                  <LabeledField id="diaSemana" label="Día de la semana">
                    <SelectField
                      id="diaSemana"
                      aria-describedby={cycleDescribedBy}
                      value={rule.anchor ? String(rule.anchor) : ''}
                      options={WEEKDAY_OPTIONS}
                      placeholder="Elige el día"
                      disabled={disabled}
                      onChange={anchor => emitRule({ ...rule, anchor: Number(anchor) })}
                    />
                  </LabeledField>
                )}

                {rule.type === 'monthday' && (
                  <LabeledField id="diaMes" label="Día del mes">
                    <NumberField
                      id="diaMes"
                      aria-describedby={cycleDescribedBy}
                      value={rule.anchor ?? null}
                      min={1}
                      max={31}
                      disabled={disabled}
                      onChange={anchor => emitRule({ ...rule, anchor: anchor ?? undefined })}
                    />
                  </LabeledField>
                )}

                {rule.type === 'range' && (
                  <>
                    <LabeledField id="inicioRango" label="Inicio del rango">
                      <DateField
                        id="inicioRango"
                        aria-describedby={cycleDescribedBy}
                        value={rule.start ?? null}
                        disabled={disabled}
                        onChange={start => emitRule({ ...rule, start: start ?? undefined })}
                      />
                    </LabeledField>
                    <LabeledField id="finRango" label="Fin del rango">
                      <DateField
                        id="finRango"
                        aria-describedby={cycleDescribedBy}
                        value={rule.end ?? null}
                        min={rule.start || undefined}
                        disabled={disabled}
                        onChange={end => emitRule({ ...rule, end: end ?? undefined })}
                      />
                    </LabeledField>
                  </>
                )}
              </Fields>

              {rule.type === 'interval' && (
                <Note>
                  Los ciclos se cuentan desde la fecha de inicio. Si la pones en el futuro, el curso no abre ningún
                  periodo hasta llegar a ella.
                </Note>
              )}

              {rule.type === 'weekday' && <Note>El curso vuelve a abrirse ese día, todas las semanas.</Note>}

              {rule.type === 'monthday' && <Note>El curso vuelve a abrirse ese día, todos los meses.</Note>}

              {rule.type === 'none' && (
                <Note>
                  El curso no se repite ni reinicia el avance de nadie. Si le pones plazo, se cuenta desde la fecha en
                  que cada empleado quedó inscrito.
                </Note>
              )}

              {rule.type === 'range' && (
                <Note>
                  Ventana de una sola vez: el curso abre en la fecha inicial y ya no se repite después del fin.
                </Note>
              )}

              {shortMonthWarning && <Note>Ojo: en los meses más cortos el periodo abre el último día del mes.</Note>}
            </>
          );
        }}
      </SettingsRow>

      <SettingsRow
        label="Plazo para realizarlo"
        help="Cuánto tiempo tiene para completarlo dentro de cada ciclo. Vacío significa que no vence."
        align="start"
      >
        {({ describedById }) => {
          const dueDescribedBy = mergeDescribedBy(describedById, allErrorDescribedBy);

          return (
            <>
              <Fields>
                <LabeledField id="cantidadPlazo" label="Cantidad del plazo">
                  <NumberField
                    id="cantidadPlazo"
                    aria-describedby={dueDescribedBy}
                    value={value.dueAmount}
                    min={1}
                    disabled={disabled}
                    onChange={dueAmount => emit({ dueAmount })}
                  />
                </LabeledField>
                <LabeledField id="unidadPlazo" label="Unidad del plazo">
                  <SelectField
                    id="unidadPlazo"
                    aria-describedby={dueDescribedBy}
                    value={value.dueUnit || ''}
                    options={DUE_UNIT_OPTIONS}
                    placeholder="Sin plazo"
                    disabled={disabled}
                    onChange={dueUnit => emit({ dueUnit: (dueUnit || null) as CourseVigencyI['dueUnit'] })}
                  />
                </LabeledField>
              </Fields>

              <Note as="div">
                <Summary>{describeRule(value)}</Summary>
                {errors.map((error, index) => (
                  <FieldError key={errorId(index)} id={errorId(index)}>
                    {error}
                  </FieldError>
                ))}
              </Note>
            </>
          );
        }}
      </SettingsRow>
    </>
  );
};

export default CourseVigencyEditor;
